import { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCreateBien, useUpdateBien, useBiens, useBien } from "@/hooks/useBiens";
import { BienUpdateSchema } from "@/schemas/bien";
import { Copy, Home, Building2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { buildDuplicateDraft, copyBienPhotos, stripTransactionSpecific } from "@/v2/lib/duplicateBien";
import type { BienPhoto } from "@/types/bien";
import { useToast } from "@/components/ui/Toast";
import { useProfiles } from "@/hooks/useProfiles";
import { validateBienPrep } from "@/lib/bienPrepValidation";
import { getCreateFormFields } from "@/v2/lib/createFormFields";
import { FormField } from "@/components/ui/FormField";
import { SelectField } from "@/components/ui/SelectField";
import { DateField } from "@/components/ui/DateField";
import { FreeTextAutocompleteField } from "@/components/ui/FreeTextAutocompleteField";
import { MultiSelectField } from "@/components/ui/MultiSelectField";
import { Modal } from "@/components/ui/Modal";
import {
  getTypesBien,
  getTypesSol,
  getCaveOptions,
  getParkingOptions,
  getGarageOptions,
  getChargesInclusesOptions,
  CLASSES_PEB,
} from "@/data/options";
import {
  getCityFromPostalCode,
  getPostalCodeFromCity,
  getRegionFromPostalCode,
} from "@/lib/postalCodeLookup";
import type { BienDraft } from "@/lib/bienPrepValidation";
import type { CreateFieldSection } from "@/v2/lib/createFormFields";
import { cn } from "@/lib/utils";

// Brouillon : disponibilite = "EN PRÉPARATION" pour activer le surlignage rouge/orange.
type Draft = BienDraft & { disponibilite: string };
// transaction 'location' d'office : la création est contextuelle depuis le
// chantier Catalogue Sales (la vente passe par BienVenteCreateFormPage).
// Plus d'`entreeGestion` (plan « deux cycles », 2026-09-15) : l'existence
// d'un cycle de sortie est un geste explicite sur la fiche (« Ouvrir un
// cycle »), pas une classification à la création.
const INITIAL_DRAFT: Draft = { transaction: "location", disponibilite: "EN PRÉPARATION" };

const SECTIONS: CreateFieldSection[] = [
  "identification",
  "dates",
  "localisation",
  "interieur",
  "annexes",
  "financier",
  "energie",
  "els",
];

export function BienCreateFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [data, setData] = useState<Draft>(INITIAL_DRAFT);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [hasEdited, setHasEdited] = useState(false);

  // Duplication (C-Level) : on arrive avec l'id du bien source via le state
  // de navigation.
  const duplicateFromId = (location.state as { duplicateFromId?: string } | null)?.duplicateFromId;

  // Choix Location/Vente : depuis le chantier Catalogue Sales (2026-07-12) la
  // création est CONTEXTUELLE (bouton du catalogue Location → cette page,
  // bouton du catalogue Vente → BienVenteCreateFormPage) — la modale de choix
  // ne survit donc QUE pour la DUPLICATION. En création simple, le choix est
  // "location" d'office.
  const [transactionChoice, setTransactionChoice] = useState<"location" | "vente" | null>(
    duplicateFromId ? null : "location",
  );

  // On pré-remplit le brouillon dès que la source est chargée ET que le choix
  // Location/Vente est fait. Champs de l'autre transaction vidés via
  // stripTransactionSpecific (fix backlog « fix vente prix/loyer »).
  const { data: source } = useBien(duplicateFromId);
  const [dupInit, setDupInit] = useState(false);
  const [dupPhotos, setDupPhotos] = useState<BienPhoto[]>([]);
  const [dupRef, setDupRef] = useState<string>("");
  useEffect(() => {
    if (!duplicateFromId || dupInit || !source || !transactionChoice) return;
    setData({
      ...(stripTransactionSpecific(buildDuplicateDraft(source), "location") as Draft),
      transaction: transactionChoice,
    });
    setDupPhotos((source.photos as BienPhoto[] | null) ?? []);
    setDupRef(source.referenceInterne || source.reference || source.adresse || "");
    setDupInit(true);
  }, [duplicateFromId, source, dupInit, transactionChoice]);

  const chooseTransaction = (choice: "location" | "vente") => {
    if (choice === "vente") {
      // Duplication vers une fiche VENTE → formulaire vente dédié (il applique
      // lui-même buildDuplicateDraft + stripTransactionSpecific(…, "vente")).
      navigate("/v2/biens-vente/new", { state: { duplicateFromId }, replace: true });
      return;
    }
    setTransactionChoice(choice);
    if (!duplicateFromId) {
      // Création simple : pas de brouillon à attendre, applique tout de suite.
      setData((prev) => ({ ...prev, transaction: choice }));
    }
    // Cas duplication : le useEffect ci-dessus applique le choix dès que la
    // fiche source est chargée (il attend explicitement transactionChoice).
  };

  const createBien = useCreateBien();
  const updateBien = useUpdateBien(createdId ?? "");

  // Options traduits
  const TYPES_BIEN = useMemo(() => getTypesBien(t), [t]);
  const TYPES_SOL = useMemo(() => getTypesSol(t), [t]);
  const CAVE_OPTIONS = useMemo(() => getCaveOptions(t), [t]);
  const PARKING_OPTIONS = useMemo(() => getParkingOptions(t), [t]);
  const GARAGE_OPTIONS = useMemo(() => getGarageOptions(t), [t]);
  const CHARGES_INCLUSES_OPTIONS = useMemo(() => getChargesInclusesOptions(t), [t]);
  const OUI_NON_OPTIONS = useMemo(
    () => [
      { value: "", label: "—" },
      { value: "oui", label: t("common.yes") },
      { value: "non", label: t("common.no") },
    ],
    [t],
  );

  // Suggestions projet (valeurs distinctes sur d'autres biens)
  const { data: allBiens = [] } = useBiens();
  const projetSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const b of allBiens) {
      const v = (b.projet ?? "").trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out.sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [allBiens]);

  // Collaborateurs SRS (role=collaborateur, actifs)
  const { data: profiles = [] } = useProfiles();
  const collaborateurOptions = useMemo(
    () =>
      profiles
        .filter((p) => p.role === "collaborateur" && p.statut !== "inactif")
        .map((p) => ({
          value: p.id,
          label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
        })),
    [profiles],
  );

  // Options ELS (prestataires spécialité état-des-lieux)
  const buildProfileOptions = useMemo(
    () =>
      (metierId: string, specials: { amiable?: boolean; autre?: boolean } = {}) => {
        const matching = profiles
          .filter((p) => p.role === "prestataire")
          .filter((p) => p.statut !== "inactif")
          .filter((p) => (p.specialites ?? []).includes(metierId))
          .map((p) => ({
            value: p.id,
            label: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        return [
          ...matching,
          ...(specials.amiable ? [{ value: "amiable", label: t("biens.expertiseAmiable") }] : []),
          ...(specials.autre ? [{ value: "autre", label: t("common.other") }] : []),
        ];
      },
    [profiles, t],
  );

  const elsOptions = useMemo(
    () => buildProfileOptions("etat-des-lieux", { amiable: true, autre: true }),
    [buildProfileOptions],
  );

  // Rouge/orange : même source que la fiche
  const prep = useMemo(() => validateBienPrep(data), [data]);
  const fields = useMemo(() => getCreateFormFields(data), [data]);

  // Setters
  const set = (key: string) => (value: string) => {
    setHasEdited(true);
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const setBoolFn = (key: string) => (s: string) => {
    setHasEdited(true);
    setData((prev) => ({
      ...prev,
      [key]: s === "oui" ? true : s === "non" ? false : null,
    }));
  };

  const boolToOption = (v: unknown): string =>
    v === true ? "oui" : v === false ? "non" : "";

  // missingHint par champ
  const mh = (key: string): "required" | "mailing" | undefined =>
    prep.byField.get(key);

  // Postal code lookup (simplifié: pas de logique quartier)
  const handleCodePostalChange = (value: string) => {
    setHasEdited(true);
    setData((prev) => {
      const updated: Draft = { ...prev, codePostal: value };
      const city = getCityFromPostalCode(value);
      const region = getRegionFromPostalCode(value);
      if (city) updated.ville = city;
      if (region) updated.region = region;
      return updated;
    });
  };

  const handleVilleChange = (value: string) => {
    setHasEdited(true);
    setData((prev) => {
      const updated: Draft = { ...prev, ville: value };
      const code = getPostalCodeFromCity(value);
      if (code) {
        updated.codePostal = code;
        const region = getRegionFromPostalCode(code);
        if (region) updated.region = region;
      }
      return updated;
    });
  };

  // ELS change : synchronise les deux champs comme BienDetailPage
  const handleElsChange = (value: string) => {
    setHasEdited(true);
    setData((prev) => ({
      ...prev,
      prestataireEls: value,
      prestataireEle: value,
    }));
  };

  // Construit le patch Zod normalisé (strip inconnu, transaction nullable).
  // Lors d'une duplication, les champs DB vides arrivent comme null JS.
  // BienUpdateSchema utilise .optional() (pas .nullable()) pour la plupart
  // des string fields → Zod rejette null. On strip null→undefined d'abord ;
  // seul `transaction` est explicitement re-positionné à null si absent.
  const buildPatch = () => {
    const patchInput: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      if (v !== null) patchInput[k] = v;
    }
    if (!patchInput.transaction) patchInput.transaction = null;
    return BienUpdateSchema.parse(patchInput);
  };

  // Autosave sur blur (après création seulement, si changements en attente)
  const handleBlur = () => {
    if (!createdId || !hasEdited || updateBien.isPending) return;
    updateBien.mutate(buildPatch(), { onSuccess: () => setHasEdited(false) });
  };

  // Création libre (bouton toujours actif, couleurs = guidage seulement)
  const handleCreate = () => {
    if (createdId || createBien.isPending) return;
    const id = crypto.randomUUID();
    const patch = buildPatch();
    createBien.mutate({ id, ...patch }, {
      onSuccess: async () => {
        setCreatedId(id);
        setHasEdited(false);
        // Duplication : copie physique des photos du bien source vers le
        // nouveau bien (best-effort), puis écriture du tableau photos.
        if (dupPhotos.length > 0) {
          try {
            const newPhotos = await copyBienPhotos(dupPhotos, id);
            if (newPhotos.length > 0) {
              await supabase.from("biens").update({ photos: newPhotos }).eq("id", id);
            }
          } catch {
            // best-effort : le bien est créé même si la copie photo échoue
          }
        }
        toast.success(t("v2.bienCreate.createdToast"));
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
    });
  };

  // Rendu d'un champ selon sa clé
  const renderField = (key: string) => {
    const hint = mh(key);
    const strVal = (k: string): string => {
      const v = data[k];
      return typeof v === "string" ? v : "";
    };

    switch (key) {
      case "referenceInterne":
        return (
          <FormField
            key={key}
            label={t("biens.internalRef")}
            value={strVal("referenceInterne")}
            onChange={set("referenceInterne")}
            missingHint={hint}
          />
        );
      case "projet":
        return (
          <FreeTextAutocompleteField
            key={key}
            label={t("biens.projet")}
            value={strVal("projet")}
            onChange={set("projet")}
            suggestions={projetSuggestions}
            missingHint={hint}
          />
        );
      case "typeBien":
        return (
          <SelectField
            key={key}
            label={t("biens.type")}
            value={strVal("typeBien")}
            onChange={set("typeBien")}
            options={TYPES_BIEN}
            missingHint={hint}
          />
        );
      // `transaction` n'a plus de champ : la création est CONTEXTUELLE (le
      // bouton du catalogue Location arrive ici avec transaction="location"
      // figée, celui du catalogue Vente ouvre BienVenteCreateFormPage). Le
      // déroulant survivant permettait de basculer en cours de saisie et
      // produisait un bien vente rempli comme une location, SANS champ prix à
      // l'écran (`prix` n'existe pas dans ce formulaire) — c'est l'origine du
      // bien 294867 (audit 2026-08-07). Convertir un bien passe désormais par
      // la duplication vers l'autre catalogue, seul chemin qui vide les champs
      // de la transaction d'origine (cf. stripTransactionSpecific).
      case "collaborateurSrs":
        return (
          <SelectField
            key={key}
            label={t("biens.collaborateurSrs")}
            value={strVal("collaborateurSrs")}
            onChange={set("collaborateurSrs")}
            options={collaborateurOptions}
            missingHint={hint}
          />
        );
      case "companyContract":
        return (
          <SelectField
            key={key}
            label={t("biens.companyContract")}
            value={boolToOption(data.companyContract)}
            onChange={setBoolFn("companyContract")}
            options={OUI_NON_OPTIONS}
            missingHint={hint}
          />
        );
      case "meuble":
        return (
          <SelectField
            key={key}
            label={t("biens.meuble")}
            value={boolToOption(data.meuble)}
            onChange={setBoolFn("meuble")}
            options={OUI_NON_OPTIONS}
            missingHint={hint}
          />
        );
      case "seraDispoPour":
        return (
          <DateField
            key={key}
            label={t("biens.availableDate")}
            value={strVal("seraDispoPour")}
            onChange={set("seraDispoPour")}
            missingHint={hint}
          />
        );
      case "renonRecuLe":
        return (
          <DateField
            key={key}
            label={t("biens.renonRecuLe")}
            value={strVal("renonRecuLe")}
            onChange={set("renonRecuLe")}
            missingHint={hint}
          />
        );
      case "finDeContrat":
        return (
          <DateField
            key={key}
            label={t("biens.finDeContrat")}
            value={strVal("finDeContrat")}
            onChange={set("finDeContrat")}
            missingHint={hint}
          />
        );
      case "codePostal":
        return (
          <FormField
            key={key}
            label={t("common.postalCode")}
            value={strVal("codePostal")}
            onChange={handleCodePostalChange}
            missingHint={hint}
          />
        );
      case "ville":
        return (
          <FormField
            key={key}
            label={t("common.city")}
            value={strVal("ville")}
            onChange={handleVilleChange}
            missingHint={hint}
          />
        );
      case "adresse":
        return (
          <FormField
            key={key}
            label={t("common.address")}
            value={strVal("adresse")}
            onChange={set("adresse")}
            missingHint={hint}
          />
        );
      case "etage":
        return (
          <FormField
            key={key}
            label={t("biens.etage")}
            value={strVal("etage")}
            onChange={set("etage")}
            missingHint={hint}
          />
        );
      case "nbChambres":
        return (
          <FormField
            key={key}
            label={t("biens.nbChambres")}
            value={strVal("nbChambres")}
            onChange={set("nbChambres")}
            missingHint={hint}
          />
        );
      case "nbSdb":
        return (
          <FormField
            key={key}
            label={t("biens.nbSdb")}
            value={strVal("nbSdb")}
            onChange={set("nbSdb")}
            missingHint={hint}
          />
        );
      case "typeSol":
        return (
          <SelectField
            key={key}
            label={t("biens.floorType")}
            value={strVal("typeSol")}
            onChange={set("typeSol")}
            options={TYPES_SOL}
            missingHint={hint}
          />
        );
      case "caveDisponible":
        return (
          <SelectField
            key={key}
            label={t("biens.cellarAvailable")}
            value={strVal("caveDisponible")}
            onChange={set("caveDisponible")}
            options={CAVE_OPTIONS}
            missingHint={hint}
          />
        );
      case "caveNumero":
        return (
          <FormField
            key={key}
            label={t("biens.cellarNumber")}
            value={strVal("caveNumero")}
            onChange={set("caveNumero")}
            missingHint={hint}
          />
        );
      case "parkingDisponible":
        return (
          <SelectField
            key={key}
            label={t("biens.parkingAvailable")}
            value={strVal("parkingDisponible")}
            onChange={set("parkingDisponible")}
            options={PARKING_OPTIONS}
            missingHint={hint}
          />
        );
      case "parkingNumero":
        return (
          <FormField
            key={key}
            label={t("biens.parkingNumber")}
            value={strVal("parkingNumero")}
            onChange={set("parkingNumero")}
            missingHint={hint}
          />
        );
      case "parkingLoyerMensuel":
        return (
          <FormField
            key={key}
            label={t("biens.parkingLoyerMensuel")}
            value={strVal("parkingLoyerMensuel")}
            onChange={set("parkingLoyerMensuel")}
            missingHint={hint}
          />
        );
      case "garageDisponible":
        return (
          <SelectField
            key={key}
            label={t("biens.garageAvailable")}
            value={strVal("garageDisponible")}
            onChange={set("garageDisponible")}
            options={GARAGE_OPTIONS}
            missingHint={hint}
          />
        );
      case "garageNumero":
        return (
          <FormField
            key={key}
            label={t("biens.garageNumber")}
            value={strVal("garageNumero")}
            onChange={set("garageNumero")}
            missingHint={hint}
          />
        );
      case "garageLoyerMensuel":
        return (
          <FormField
            key={key}
            label={t("biens.garageLoyerMensuel")}
            value={strVal("garageLoyerMensuel")}
            onChange={set("garageLoyerMensuel")}
            missingHint={hint}
          />
        );
      case "loyerMensuel":
        return (
          <FormField
            key={key}
            label={t("biens.loyerMensuel")}
            value={strVal("loyerMensuel")}
            onChange={set("loyerMensuel")}
            missingHint={hint}
          />
        );
      case "chargesMensuelles":
        return (
          <FormField
            key={key}
            label={t("biens.chargesMensuelles")}
            value={strVal("chargesMensuelles")}
            onChange={set("chargesMensuelles")}
            missingHint={hint}
          />
        );
      case "chargesIncluses":
        return (
          <MultiSelectField
            key={key}
            label={t("biens.chargesIncluded")}
            value={Array.isArray(data.chargesIncluses) ? (data.chargesIncluses as string[]) : []}
            onChange={(v) => { setHasEdited(true); setData((prev) => ({ ...prev, chargesIncluses: v })); }}
            options={CHARGES_INCLUSES_OPTIONS}
            missingHint={hint}
          />
        );
      case "classePeb":
        return (
          <SelectField
            key={key}
            label={t("biens.pebClass")}
            value={strVal("classePeb")}
            onChange={set("classePeb")}
            options={CLASSES_PEB}
            missingHint={hint}
          />
        );
      case "pebFinLe":
        return (
          <DateField
            key={key}
            label={t("biens.pebExpiryDate")}
            value={strVal("pebFinLe")}
            onChange={set("pebFinLe")}
            missingHint={hint}
          />
        );
      case "consoKwh":
        return (
          <FormField
            key={key}
            label={t("biens.consumption")}
            value={strVal("consoKwh")}
            onChange={set("consoKwh")}
            missingHint={hint}
          />
        );
      case "prestataireEls":
        return (
          <SelectField
            key={key}
            label={t("biens.prestataireEls")}
            value={strVal("prestataireEls")}
            onChange={handleElsChange}
            options={elsOptions}
            missingHint={hint}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      {/* Choix Location/Vente — bloquant, avant tout le reste (audit process
          2026-07-10, retrait de "location_vente"). Couvre création ET
          duplication (cf. chooseTransaction). */}
      <Modal
        isOpen={transactionChoice === null}
        onClose={() => navigate("/v2/biens")}
        title={t("v2.bienCreate.transactionChoiceTitle", "Ce bien est pour...")}
      >
        <p className="text-sm text-text-secondary mb-4">
          {t(
            "v2.bienCreate.transactionChoiceBody",
            "Choisis le type de transaction — ce choix ne combine plus location et vente.",
          )}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => chooseTransaction("location")}
            className="flex-1 flex flex-col items-center gap-2 px-4 py-5 rounded-lg border border-border bg-surface hover:border-accent hover:bg-accent/5 cursor-pointer outline-none shadow-none transition-colors"
          >
            <Home size={22} className="text-accent" />
            <span className="text-sm font-medium text-text-primary">{t("biens.transactionType.location")}</span>
          </button>
          <button
            type="button"
            onClick={() => chooseTransaction("vente")}
            className="flex-1 flex flex-col items-center gap-2 px-4 py-5 rounded-lg border border-border bg-surface hover:border-accent hover:bg-accent/5 cursor-pointer outline-none shadow-none transition-colors"
          >
            <Building2 size={22} className="text-accent" />
            <span className="text-sm font-medium text-text-primary">{t("biens.transactionType.vente")}</span>
          </button>
        </div>
      </Modal>

      {transactionChoice !== null && (
        <>
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-text">{t("v2.bienCreate.title")}</h1>
            <p className="mt-1 text-sm text-text-muted">{t("v2.bienCreate.subtitle")}</p>
            {dupRef && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-accent/30 bg-accent/5 px-3 py-2 text-sm text-text-secondary">
                <Copy size={15} className="text-accent flex-shrink-0 mt-0.5" />
                <span>
                  {t("v2.bienCreate.duplicateBanner", "Duplication de {{ref}}. Saisis une nouvelle référence interne ; le contenu (photos, descriptions, champs) est repris, l'historique ELS/PEB et la publication sont réinitialisés.", { ref: dupRef })}
                </span>
              </div>
            )}
          </div>

          {/* Sections */}
          <div className="space-y-5" onBlur={handleBlur}>
            {SECTIONS.map((section) => {
              const sectionFields = fields.filter((f) => f.section === section);
              if (sectionFields.length === 0) return null;
              return (
                <div
                  key={section}
                  className="bg-surface border border-border rounded-lg p-5"
                >
                  <h2 className="text-xs font-medium tracking-wider uppercase text-text-muted mb-4">
                    {t(`v2.bienCreate.sections.${section}`)}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {sectionFields.map((f) => renderField(f.key))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center gap-4">
            {!createdId && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={createBien.isPending}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium outline-none shadow-none transition-colors",
                  "bg-accent text-white hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {t("v2.bienCreate.createButton")}
              </button>
            )}
            {createdId && (
              <button
                type="button"
                onClick={() => navigate(`/v2/biens/${createdId}`)}
                className="text-sm text-accent underline underline-offset-2 outline-none shadow-none hover:opacity-80 transition-opacity"
              >
                {t("v2.bienCreate.openFullSheet")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
